import React, { useCallback, useContext, useEffect, useState } from "react";
import {
  Box,
  Button,
  Chip,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import RefreshIcon from "@material-ui/icons/Refresh";
import FileCopyOutlinedIcon from "@material-ui/icons/FileCopyOutlined";
import { toast } from "react-toastify";
import { format, parseISO, subDays } from "date-fns";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import ForbiddenPage from "../../components/ForbiddenPage";
import toastError from "../../errors/toastError";
import api from "../../services/api";
import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(2),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
  section: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  statCard: {
    padding: theme.spacing(2),
    height: "100%",
  },
  statValue: {
    fontSize: 26,
    fontWeight: 600,
  },
  sectionTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(1.5),
  },
  bar: {
    height: 8,
    borderRadius: 4,
    background: theme.palette.primary.main,
  },
  ua: {
    maxWidth: 280,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    fontSize: 12,
  },
}));

const OUTCOME_LABEL = {
  redirected: "Rodízio",
  fallback: "Número reserva",
  unavailable: "Indisponível",
};

const today = () => format(new Date(), "yyyy-MM-dd");

const AttendanceLink = () => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);

  const [config, setConfig] = useState({ active: true, defaultText: "", fallbackNumber: "", url: "" });
  const [connections, setConnections] = useState([]);
  const [queues, setQueues] = useState([]);
  const [saving, setSaving] = useState(false);
  const [startDate, setStartDate] = useState(format(subDays(new Date(), 6), "yyyy-MM-dd"));
  const [endDate, setEndDate] = useState(today());
  const [queueFilter, setQueueFilter] = useState("");
  const [metrics, setMetrics] = useState(null);

  const loadOverview = useCallback(async () => {
    try {
      const { data } = await api.get("/attendance-link");
      setConfig(data.config);
      setConnections(data.connections);
      setQueues(data.queues || []);
    } catch (err) {
      toastError(err);
    }
  }, []);

  const loadMetrics = useCallback(async () => {
    try {
      const { data } = await api.get("/attendance-link/metrics", {
        params: { startDate, endDate, queueId: queueFilter || undefined },
      });
      setMetrics(data);
    } catch (err) {
      toastError(err);
    }
  }, [startDate, endDate, queueFilter]);

  useEffect(() => {
    loadOverview();
  }, [loadOverview]);

  useEffect(() => {
    loadMetrics();
  }, [loadMetrics]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/attendance-link", config);
      setConfig(data);
      toast.success("Configuração salva");
      loadOverview();
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  const copy = (value) => {
    navigator.clipboard
      .writeText(value)
      .then(() => toast.success("Copiado"))
      .catch(() => toast.error("Não foi possível copiar"));
  };

  if (user.profile !== "admin") {
    return (
      <MainContainer>
        <ForbiddenPage />
      </MainContainer>
    );
  }

  const inRotation = connections.filter((c) => c.inRotation).length;
  const maxByNumber = metrics ? Math.max(1, ...metrics.byNumber.map((r) => r.clicks)) : 1;
  const exampleWithText = config.url ? `${config.url}?text=Olá, vim pelo link` : "";
  const exampleTemplate = config.url ? `${config.url}?ref={{1}}` : "";

  const statCards = metrics
    ? [
        { label: "Cliques", value: metrics.totals.clicks },
        { label: "Rodízio", value: metrics.totals.redirected },
        { label: "Número reserva", value: metrics.totals.fallback },
        { label: "Indisponível", value: metrics.totals.unavailable },
        { label: "Robôs (prévia)", value: metrics.totals.bots },
      ]
    : [];

  return (
    <MainContainer>
      <MainHeader>
        <Title>Link de atendimento</Title>
        <MainHeaderButtonsWrapper>
          <Tooltip title="Atualizar">
            <IconButton
              onClick={() => {
                loadOverview();
                loadMetrics();
              }}
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <Paper className={classes.mainPaper} variant="outlined">
        <Paper variant="outlined" className={classes.section}>
          <Typography className={classes.sectionTitle}>Configuração</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                label="Link público"
                value={config.url}
                fullWidth
                variant="outlined"
                size="small"
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => copy(config.url)}>
                        <FileCopyOutlinedIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <Typography variant="caption" color="textSecondary" component="div" style={{ marginTop: 6 }}>
                Mensagem pré-preenchida: <code>{exampleWithText}</code>
                <br />
                Botão de URL dinâmica (template Meta): <code>{exampleTemplate}</code> — o parâmetro{" "}
                <code>ref</code> aparece nas métricas.
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Mensagem padrão (quando o link não traz ?text=)"
                value={config.defaultText}
                onChange={(e) => setConfig({ ...config, defaultText: e.target.value })}
                fullWidth
                multiline
                minRows={2}
                variant="outlined"
                size="small"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label="Número reserva (sem conexão online)"
                placeholder="5511999999999"
                value={config.fallbackNumber}
                onChange={(e) => setConfig({ ...config, fallbackNumber: e.target.value })}
                fullWidth
                variant="outlined"
                size="small"
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControlLabel
                control={
                  <Switch
                    color="primary"
                    checked={config.active}
                    onChange={(e) => setConfig({ ...config, active: e.target.checked })}
                  />
                }
                label={config.active ? "Ativo" : "Desativado"}
              />
            </Grid>
            <Grid item xs={12}>
              <Button variant="contained" color="primary" disabled={saving} onClick={handleSave}>
                Salvar
              </Button>
            </Grid>
          </Grid>
        </Paper>

        <Paper variant="outlined" className={classes.section}>
          <Typography className={classes.sectionTitle}>Links por fila</Typography>
          <Typography variant="caption" color="textSecondary" component="div" style={{ marginBottom: 8 }}>
            O rodízio considera só as conexões que têm a fila como redirecionamento padrão. Sem conexão online na
            fila, o cliente vai para o número reserva.
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Fila</TableCell>
                <TableCell>Link</TableCell>
                <TableCell align="right">Conexões no rodízio</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {queues.map((q) => (
                <TableRow key={q.id}>
                  <TableCell>
                    {q.name}
                    <Typography variant="caption" color="textSecondary" component="div">
                      ID {q.id}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" style={{ gap: 4 }}>
                      <code style={{ wordBreak: "break-all" }}>{q.url}</code>
                      <IconButton size="small" onClick={() => copy(q.url)}>
                        <FileCopyOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </TableCell>
                  <TableCell align="right">
                    {q.connections === 0 ? (
                      <Chip size="small" variant="outlined" label="Nenhuma conexão" />
                    ) : (
                      `${q.inRotation} de ${q.connections}`
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {queues.length === 0 && (
                <TableRow>
                  <TableCell colSpan={3} align="center">
                    Nenhuma fila cadastrada
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Paper>

        <Paper variant="outlined" className={classes.section}>
          <Typography className={classes.sectionTitle}>
            Conexões no rodízio ({inRotation} de {connections.length})
          </Typography>
          <Typography variant="caption" color="textSecondary" component="div" style={{ marginBottom: 8 }}>
            O próximo clique vai para a conexão online com menor carga (tickets abertos + cliques dos últimos 30 minutos).
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Conexão</TableCell>
                <TableCell>Número</TableCell>
                <TableCell>Fila de redirecionamento</TableCell>
                <TableCell>Situação</TableCell>
                <TableCell align="right">Tickets abertos</TableCell>
                <TableCell align="right">Cliques recentes</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {connections.map((c) => (
                <TableRow key={c.id}>
                  <TableCell>{c.name}</TableCell>
                  <TableCell>{c.number || "-"}</TableCell>
                  <TableCell>{c.sendQueueName || "-"}</TableCell>
                  <TableCell>
                    {c.inRotation ? (
                      <Chip size="small" color="primary" label="No rodízio" />
                    ) : (
                      <Chip
                        size="small"
                        label={c.status === "CONNECTED" && !c.alive ? "Sessão caída" : "Fora do ar"}
                      />
                    )}
                  </TableCell>
                  <TableCell align="right">{c.openTickets ?? "-"}</TableCell>
                  <TableCell align="right">{c.recentClicks ?? "-"}</TableCell>
                </TableRow>
              ))}
              {connections.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    Nenhuma conexão WhatsApp cadastrada
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Paper>

        <Paper variant="outlined" className={classes.section}>
          <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" mb={2} style={{ gap: 8 }}>
            <Typography className={classes.sectionTitle} style={{ marginBottom: 0 }}>
              Métricas
            </Typography>
            <Box display="flex" flexWrap="wrap" style={{ gap: 8 }}>
              <FormControl size="small" variant="outlined" style={{ minWidth: 180 }}>
                <InputLabel id="queue-filter-label">Link</InputLabel>
                <Select
                  labelId="queue-filter-label"
                  label="Link"
                  value={queueFilter}
                  onChange={(e) => setQueueFilter(e.target.value)}
                >
                  <MenuItem value="">Todos</MenuItem>
                  <MenuItem value="general">Link geral</MenuItem>
                  {queues.map((q) => (
                    <MenuItem key={q.id} value={String(q.id)}>
                      Fila: {q.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                type="date"
                label="De"
                size="small"
                variant="outlined"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                type="date"
                label="Até"
                size="small"
                variant="outlined"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Box>
          </Box>

          <Grid container spacing={2} style={{ marginBottom: 16 }}>
            {statCards.map((card) => (
              <Grid item xs={6} sm={4} md key={card.label}>
                <Paper variant="outlined" className={classes.statCard}>
                  <Typography variant="body2" color="textSecondary">
                    {card.label}
                  </Typography>
                  <Typography className={classes.statValue}>{card.value.toLocaleString("pt-BR")}</Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>

          {metrics && (
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" gutterBottom>
                  Por número
                </Typography>
                <Table size="small">
                  <TableBody>
                    {metrics.byNumber.map((r) => (
                      <TableRow key={`${r.whatsappId}-${r.number}`}>
                        <TableCell>
                          {r.name || "Número reserva"}
                          <Typography variant="caption" color="textSecondary" component="div">
                            {r.number}
                          </Typography>
                        </TableCell>
                        <TableCell style={{ width: "45%" }}>
                          <div className={classes.bar} style={{ width: `${(r.clicks / maxByNumber) * 100}%` }} />
                        </TableCell>
                        <TableCell align="right">{r.clicks}</TableCell>
                      </TableRow>
                    ))}
                    {metrics.byNumber.length === 0 && (
                      <TableRow>
                        <TableCell align="center">Sem cliques no período</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </Grid>

              <Grid item xs={12} md={3}>
                <Typography variant="subtitle2" gutterBottom>
                  Por dia
                </Typography>
                <Table size="small">
                  <TableBody>
                    {metrics.byDay.map((r) => (
                      <TableRow key={r.day}>
                        <TableCell>{format(parseISO(r.day), "dd/MM/yyyy")}</TableCell>
                        <TableCell align="right">{r.clicks}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Grid>

              <Grid item xs={12} md={3}>
                <Typography variant="subtitle2" gutterBottom>
                  Por link
                </Typography>
                <Table size="small">
                  <TableBody>
                    {metrics.byQueue.map((r) => (
                      <TableRow key={r.queueId || "general"}>
                        <TableCell>{r.queueId ? `Fila: ${r.name || r.queueId}` : "Link geral"}</TableCell>
                        <TableCell align="right">{r.clicks}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>

                <Typography variant="subtitle2" gutterBottom style={{ marginTop: 16 }}>
                  Por origem (ref)
                </Typography>
                <Table size="small">
                  <TableBody>
                    {metrics.byRef.map((r) => (
                      <TableRow key={r.ref || "-"}>
                        <TableCell>{r.ref || "(sem ref)"}</TableCell>
                        <TableCell align="right">{r.clicks}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom style={{ marginTop: 8 }}>
                  Últimos acessos
                </Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Data</TableCell>
                      <TableCell>Link</TableCell>
                      <TableCell>Destino</TableCell>
                      <TableCell>Resultado</TableCell>
                      <TableCell>Ref</TableCell>
                      <TableCell>Navegador</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {metrics.recent.map((c) => (
                      <TableRow key={c.id}>
                        <TableCell>{format(parseISO(c.createdAt), "dd/MM/yyyy HH:mm:ss")}</TableCell>
                        <TableCell>{c.queueId ? `Fila: ${c.queue?.name || c.queueId}` : "Geral"}</TableCell>
                        <TableCell>{c.number || "-"}</TableCell>
                        <TableCell>
                          {c.isBot ? (
                            <Chip size="small" variant="outlined" label="Robô" />
                          ) : (
                            OUTCOME_LABEL[c.outcome] || c.outcome
                          )}
                        </TableCell>
                        <TableCell>{c.ref || "-"}</TableCell>
                        <TableCell>
                          <Tooltip title={c.userAgent || ""}>
                            <div className={classes.ua}>{c.userAgent || "-"}</div>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                    {metrics.recent.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={6} align="center">
                          Nenhum acesso registrado
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </Grid>
            </Grid>
          )}
        </Paper>
      </Paper>
    </MainContainer>
  );
};

export default AttendanceLink;
